import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { apiClient } from "@/services/api";
import type { TeamLiveStreamEntry } from "@/types";

// The multiview is a monitoring surface: poll every few seconds so tiles
// appear/disappear quickly as members start and stop Trackcast. The empty
// wall polls only slightly slower — first-tile latency matters more than the
// small idle saving, and each poll is a handful of indexed queries.
const TEAM_LIVE_POLL_INTERVAL_MS = 3_000;
const TEAM_LIVE_EMPTY_POLL_INTERVAL_MS = 5_000;

export function useTeamLiveWall(teamId: string) {
  return useQuery({
    queryKey: ["team-live-wall", teamId],
    queryFn: () => apiClient.getTeamLiveWall(teamId),
    enabled: !!teamId,
    refetchInterval: (query) =>
      query.state.data?.length
        ? TEAM_LIVE_POLL_INTERVAL_MS
        : TEAM_LIVE_EMPTY_POLL_INTERVAL_MS,
    // The wall is a monitoring surface often left on an unattended monitor;
    // react-query otherwise pauses the interval while the tab is unfocused
    // (hidden or covered) and tiles go stale. Browser timer throttling still
    // applies in background tabs; live WHEP connections exempt the page from
    // intensive throttling.
    refetchIntervalInBackground: true,
  });
}

// Recording and focus endpoints are session-scoped. Unassigned captures
// (session null) never offer these controls (canRecord is false), so reaching
// here without a session is a caller bug.
function requireSessionId(entry: TeamLiveStreamEntry): string {
  if (!entry.session) {
    throw new Error(`Live stream ${entry.liveStream.id} has no session`);
  }
  return entry.session.id;
}

export function useSetLiveStreamRecording(teamId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation("pages");
  // Pending toggles are tracked per live-stream id so one in-flight request
  // only disables that tile's control, not every tile on the wall.
  const [pendingStreamIds, setPendingStreamIds] = useState<Set<string>>(
    new Set(),
  );

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: ["team-live-wall", teamId],
      }),
    [queryClient, teamId],
  );

  const mutation = useMutation({
    mutationFn: ({
      entry,
      desired,
    }: {
      entry: TeamLiveStreamEntry;
      desired: boolean;
    }) =>
      apiClient.setLiveStreamRecording(
        requireSessionId(entry),
        entry.liveStream.id,
        desired,
      ),
    onMutate: ({ entry }) => {
      setPendingStreamIds((prev) => new Set(prev).add(entry.liveStream.id));
    },
    onSettled: (_data, _error, { entry }) => {
      setPendingStreamIds((prev) => {
        const next = new Set(prev);
        next.delete(entry.liveStream.id);
        return next;
      });
      invalidate();
    },
  });

  // Record-all / stop-all fire one request per entry; per-entry pending
  // tracking and invalidation apply, and a summary toast makes a partial
  // outage visible instead of silent.
  const mutateAll = useCallback(
    async (entries: TeamLiveStreamEntry[], desired: boolean) => {
      if (entries.length === 0) return;

      setPendingStreamIds((prev) => {
        const next = new Set(prev);
        for (const entry of entries) next.add(entry.liveStream.id);
        return next;
      });

      const results = await Promise.allSettled(
        entries.map(async (entry) =>
          apiClient.setLiveStreamRecording(
            requireSessionId(entry),
            entry.liveStream.id,
            desired,
          ),
        ),
      );

      setPendingStreamIds((prev) => {
        const next = new Set(prev);
        for (const entry of entries) next.delete(entry.liveStream.id);
        return next;
      });

      const failed = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected",
      ).length;
      if (failed > 0) {
        toast.error(
          t("teamLive.bulkRecordingFailed", {
            failed,
            total: entries.length,
          }),
        );
      }
      invalidate();
    },
    [t, invalidate],
  );

  const isToggling = useCallback(
    (entry: TeamLiveStreamEntry) =>
      pendingStreamIds.has(entry.liveStream.id),
    [pendingStreamIds],
  );

  return { ...mutation, mutateAll, isToggling };
}

/**
 * Wall remote cam control (2026-09-17 spec): director cam on/off per device.
 * Per-device pending tracking like recording; errors toast — the wall has no
 * other global error surface. Bulk fires one request per device so a partial
 * outage is visible instead of silent.
 */
export function useSetDevicePublication(teamId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation("pages");
  const [pendingDeviceIds, setPendingDeviceIds] = useState<Set<string>>(
    new Set(),
  );

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: ["team-live-wall", teamId],
      }),
    [queryClient, teamId],
  );

  const mutation = useMutation({
    mutationFn: ({
      deviceId,
      on,
      record = false,
    }: {
      deviceId: string;
      on: boolean;
      /** true = the cam-on GO LIVE should also start recording. */
      record?: boolean;
    }) => apiClient.setDevicePublicationControl(teamId, deviceId, on, record),
    onMutate: ({ deviceId }) => {
      setPendingDeviceIds((prev) => new Set(prev).add(deviceId));
    },
    onSettled: (_data, _error, { deviceId }) => {
      setPendingDeviceIds((prev) => {
        const next = new Set(prev);
        next.delete(deviceId);
        return next;
      });
      invalidate();
    },
    onError: () => {
      toast.error(t("teamLive.publicationFailed"));
    },
  });

  const mutateAll = useCallback(
    async (targets: { deviceId: string }[], on: boolean) => {
      if (targets.length === 0) return;

      setPendingDeviceIds((prev) => {
        const next = new Set(prev);
        for (const target of targets) next.add(target.deviceId);
        return next;
      });

      const results = await Promise.allSettled(
        targets.map((target) =>
          apiClient.setDevicePublicationControl(
            teamId,
            target.deviceId,
            on,
            false,
          ),
        ),
      );

      setPendingDeviceIds((prev) => {
        const next = new Set(prev);
        for (const target of targets) next.delete(target.deviceId);
        return next;
      });

      const failed = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected",
      ).length;
      if (failed > 0) {
        toast.error(
          t("teamLive.bulkPublicationFailed", {
            failed,
            total: targets.length,
          }),
        );
      }
      invalidate();
    },
    [t, invalidate, teamId],
  );

  const isRequesting = useCallback(
    (deviceId: string) => pendingDeviceIds.has(deviceId),
    [pendingDeviceIds],
  );

  return { ...mutation, mutateAll, isRequesting };
}

/**
 * Director focus declarations (trackcast #328). Per-stream pending tracking
 * like recording; errors toast — the wall has no other global error surface.
 */
export function useFocusLiveStream(teamId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation("pages");
  const [pendingStreamIds, setPendingStreamIds] = useState<Set<string>>(
    new Set(),
  );

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: ["team-live-wall", teamId],
      }),
    [queryClient, teamId],
  );

  const mutation = useMutation({
    mutationFn: ({
      entry,
      x,
      y,
    }: {
      entry: TeamLiveStreamEntry;
      x: number;
      y: number;
    }) =>
      apiClient.focusLiveStream(
        requireSessionId(entry),
        entry.liveStream.id,
        x,
        y,
      ),
    onMutate: ({ entry }) => {
      setPendingStreamIds((prev) => new Set(prev).add(entry.liveStream.id));
    },
    onSettled: (_data, _error, { entry }) => {
      setPendingStreamIds((prev) => {
        const next = new Set(prev);
        next.delete(entry.liveStream.id);
        return next;
      });
      invalidate();
    },
    onError: () => {
      toast.error(t("teamLive.focusFailed"));
    },
  });

  const isFocusing = useCallback(
    (entry: TeamLiveStreamEntry) => pendingStreamIds.has(entry.liveStream.id),
    [pendingStreamIds],
  );

  return { ...mutation, isFocusing };
}
